class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close('escape'));
    // Event delegation: works even after innerHTML replacement in renderContents()
    this.addEventListener('click', (evt) => {
      if (evt.target.id === 'CartDrawer-Overlay' || evt.target.classList.contains('cart-drawer__overlay')) {
        this.close('overlay');
      }
    });
    // Track close button click
    this.addEventListener('click', (evt) => {
      if (evt.target.closest('.close-drawer-btn')) {
        this.close('button');
      }
    });
    // Track checkout button click
    this.addEventListener('click', (evt) => {
      if (evt.target.closest('#CartDrawer-Checkout')) {
        this._trackEvent('cart_checkout_click', {
          checkout_source: 'cart_drawer',
          cart_value: this._getCartTotal(),
          item_count: this._getCartItemCount()
        });
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  _trackEvent(eventName, params) {
    if (typeof gtag === 'function') {
      gtag('event', eventName, params);
    }
  }

  _getCartTotal() {
    const el = this.querySelector('.totals__total-value .money');
    return el ? el.textContent.trim() : '';
  }

  _getCartItemCount() {
    const bubble = this.querySelector('.cart-count-bubble span[aria-hidden]');
    return bubble ? parseInt(bubble.textContent) || 0 : 0;
  }

  _lastShippingFree = null;

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this._trackEvent('cart_drawer_open', {
      cart_value: this._getCartTotal(),
      item_count: this._getCartItemCount(),
      trigger: triggeredBy ? 'icon' : 'atc'
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close(method) {
    this._trackEvent('cart_drawer_close', {
      close_method: method || 'unknown',
      cart_value: this._getCartTotal(),
      item_count: this._getCartItemCount()
    });
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const newHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (sectionElement && newHTML !== null) {
        sectionElement.innerHTML = newHTML;
      }
    });

    requestAnimationFrame(() => {
      this.open();
    });

    // Section rendering may return stale footer HTML.
    // Fetch /cart.js for authoritative data and update the footer.
    this._refreshFooterFromCart();
  }

  _refreshFooterFromCart() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const footer = this.querySelector('.cart-drawer__footer');
        const totalEl = footer?.querySelector('.totals__total-value');
        if (!totalEl) return;

        const fmt = window.theme?.settings?.money_with_currency_format || '${{amount}}';
        const hasDiscount = cart.total_discount > 0;
        const originalPrice = cart.original_total_price || cart.items_subtotal_price;

        if (hasDiscount && originalPrice) {
          totalEl.innerHTML =
            `<s data-cart-total="${originalPrice}"><span style="opacity: 0.5;" class="money mw-price">${formatMoney(originalPrice, fmt)}</span></s>` +
            `<span class="money mw-price">${formatMoney(cart.total_price, fmt)}</span>`;
        } else {
          totalEl.innerHTML = `<span class="money mw-price">${formatMoney(cart.total_price, fmt)}</span>`;
        }

        const threshold = 3900; // $39 free shipping threshold
        const free = cart.total_price >= threshold;
        const remaining = free ? 0 : threshold - cart.total_price;
        let shippingNote = footer.querySelector('.totals__shipping-note');
        if (!shippingNote) {
          shippingNote = document.createElement('p');
          totalEl.parentElement.appendChild(shippingNote);
        }
        shippingNote.className = `totals__shipping-note totals__shipping-note--${free ? 'free' : 'paid'}`;
        shippingNote.textContent = free ? 'FREE shipping' : `+ ${formatMoney(699, fmt)} shipping`;

        // Only track when free shipping status changes
        if (this._lastShippingFree !== null && this._lastShippingFree !== free) {
          this._trackEvent('cart_shipping_progress', {
            cart_total: cart.total_price / 100,
            free_shipping: free,
            remaining_to_free: remaining / 100,
            item_count: cart.item_count
          });
        }
        this._lastShippingFree = free;
      })
      .catch(() => {});
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
